import { Injectable } from '@angular/core';

/**
 * Keys that, before this service existed, lived in `localStorage` with no
 * user attached: on the same browser the next user inherited the previous
 * one's filters, page, admin overview cache and theme. They are removed on
 * the first login because nobody knows whose they are.
 */
const LEGACY_UNSCOPED_KEYS = [
  'sg:call-search',
  'sg:semantic-search',
  'sg:geo-search',
  'sg:artifacts',
  'sg_admin_overview',
  'sg_user_color_theme',
];

/**
 * `localStorage` bound to the logged-in user: every key is prefixed with the
 * Saragraph profile id, so two users sharing a browser never see each
 * other's state.
 *
 * No dependencies on purpose: `AuthService` tells it who is logged in
 * (`setUser`), otherwise ThemeService → UserStorage → AuthService →
 * ThemeService would be a cycle. Until the user is known (before login, or
 * when the profile failed to load) reads return `null` and writes are
 * dropped: better to remember nothing than to remember for the wrong person.
 *
 * The language (`lang`) stays out of it: it is needed before login, by nginx
 * and the identity portal, and it is a browser choice more than a user one.
 */
@Injectable({ providedIn: 'root' })
export class UserStorageService {
  private prefix: string | null = null;

  setUser(userId: number): void {
    this.prefix = `sg:u:${userId}:`;
    try {
      LEGACY_UNSCOPED_KEYS.forEach(k => localStorage.removeItem(k));
    } catch { /* storage unavailable */ }
  }

  get(key: string): string | null {
    if (!this.prefix) return null;
    try { return localStorage.getItem(this.prefix + key); } catch { return null; }
  }

  set(key: string, value: string): void {
    if (!this.prefix) return;
    try { localStorage.setItem(this.prefix + key, value); } catch { /* quota */ }
  }

  remove(key: string): void {
    if (!this.prefix) return;
    try { localStorage.removeItem(this.prefix + key); } catch { /* storage unavailable */ }
  }
}
